import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/auth'
import { useTenantStore } from './stores/tenant'
import { startSyncIntervals } from './stores/sync'

// App routes
import LoginPage from './pages/LoginPage'
import DesktopShell from './pages/DesktopShell'
import MobileLayout from './pages/MobileLayout'
import PostingLayout from './pages/PostingLayout'
import DashboardPage from './pages/DashboardPage'
import SetupPasswordPage from './pages/SetupPasswordPage'

// Marketing routes (castellan-security.com main domain only)
import LandingPage from './pages/LandingPage'
import FindAgencyPage from './pages/FindAgencyPage'
import PricingPage from './pages/PricingPage'
import FeaturesPage from './pages/FeaturesPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import SignupPage from './pages/SignupPage'
import SignupSuccess from './pages/SignupSuccess'
import DownloadPage from './pages/DownloadPage'
import DocsPage from './pages/DocsPage'

// ── Detect context ─────────────────────────────────────────────────────────
// isTenantApp = true when running as:
//   • A tenant subdomain  (fom2.castellan-security.com)
//   • Native mobile app   (Capacitor: capacitor:// or https://localhost)
//   • Electron desktop    (file://)
// In all these cases show only the app — no marketing pages.
//
// isTenantApp = false only on the main marketing domain (castellan-security.com)
function detectContext(): { isTenantApp: boolean; tenantSlug: string } {
  const host  = window.location.hostname
  const proto = window.location.protocol

  // Native (Capacitor / Electron)
  if (
    proto === 'file:' ||
    proto === 'capacitor:' ||
    (host === 'localhost' && !window.location.port)
  ) {
    // Store tenant slug so API calls send the right X-Tenant-ID header
    if (!localStorage.getItem('castellan_tenant')) {
      localStorage.setItem('castellan_tenant', 'fom2')
    }
    return { isTenantApp: true, tenantSlug: 'fom2' }
  }

  // Tenant subdomain  e.g. fom2.castellan-security.com
  const parts = host.split('.')
  if (parts.length >= 3 && parts[parts.length - 2] === 'castellan-security') {
    const slug = parts[0]
    localStorage.setItem('castellan_tenant', slug)
    return { isTenantApp: true, tenantSlug: slug }
  }

  // Main domain — marketing site only
  return { isTenantApp: false, tenantSlug: '' }
}

const { isTenantApp } = detectContext()

function App() {
  const session           = useAuthStore(s => s.session)
  const fetchTenantConfig = useTenantStore(s => s.fetchConfig)
  const isMobile          = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)

  useEffect(() => {
    fetchTenantConfig()
  }, [fetchTenantConfig])

  useEffect(() => {
    if (!session) return
    const stop = startSyncIntervals()
    return stop
  }, [session])

  // ── TENANT APP (subdomain, mobile, Electron) ────────────────────────────
  // Pure app — no marketing pages exist at all on this context.
  if (isTenantApp) {
    if (!session) {
      // Save the exact page the user was on so we redirect back after login.
      // This keeps dashboard on dashboard and desktop on desktop after timeout.
      const currentHash = window.location.hash.replace('#', '') || '/'
      if (currentHash !== '/login' && currentHash !== '/') {
        sessionStorage.setItem('castellan_intended_route', currentHash)
      }
      return (
        <Routes>
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/setup-password"  element={<SetupPasswordPage />} />
          <Route path="*"                element={<Navigate to="/login" replace />} />
        </Routes>
      )
    }
    return (
      <Routes>
        <Route path="/dashboard"   element={<DashboardPage />} />
        <Route path="/desktop/*"   element={<DesktopShell />} />
        <Route path="/mobile/*"    element={<MobileLayout />} />
        <Route path="/posting/*"   element={<PostingLayout />} />
        <Route path="*"            element={<Navigate to={isMobile ? '/mobile' : '/desktop'} replace />} />
      </Routes>
    )
  }

  // ── MAIN DOMAIN — marketing only ────────────────────────────────────────
  // castellan-security.com has no app. Login redirects to find-agency.
  const marketing = (
    <>
      <Route path="/"               element={<LandingPage />} />
      <Route path="/pricing"        element={<PricingPage />} />
      <Route path="/features"       element={<FeaturesPage />} />
      <Route path="/docs"           element={<DocsPage />} />
      <Route path="/docs/:section"  element={<DocsPage />} />
      <Route path="/download"       element={<DownloadPage />} />
      <Route path="/privacy"        element={<PrivacyPage />} />
      <Route path="/terms"          element={<TermsPage />} />
      <Route path="/find-agency"    element={<FindAgencyPage />} />
      <Route path="/signup"         element={<SignupPage />} />
      <Route path="/signup/success" element={<SignupSuccess />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      {/* No /login, /desktop, /mobile, /dashboard on main domain */}
      <Route path="*"               element={<Navigate to="/" replace />} />
    </>
  )

  return <Routes>{marketing}</Routes>
}

export default App
